"use client";

import { c as _c } from "react/compiler-runtime";
import { useBuyerPropertiesStore, type BuyerUnit } from "@/stores/buyer/buyer-properties-store";
import { type BuyerProject } from "@/types/buyerProject";
import { useEffect, useState } from "react";
import { useAuth } from "./use-auth";
import { useBuyerDashboard } from "./use-buyer-dashboard";

/**
 * Global hook to initialize buyer properties store after authentication
 * This should be called at a high level (like in the main layout or after auth provider)
 * to ensure buyer properties data is available across the entire app
 */
export function useGlobalBuyerProperties() {
  const $ = _c(13);
  const {
    isAuthenticated
  } = useAuth();
  const {
    setBuyerUnits,
    setBuyerProjects
  } = useBuyerPropertiesStore();
  const {
    rawBuyerUnits,
    rawBuyerProjects,
    buyerGroupId
  } = useBuyerDashboard();
  const [isHydrated, setIsHydrated] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsHydrated(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== buyerGroupId || $[3] !== isAuthenticated || $[4] !== isHydrated || $[5] !== rawBuyerProjects || $[6] !== rawBuyerUnits || $[7] !== setBuyerProjects || $[8] !== setBuyerUnits) {
    t2 = () => {
      if (isHydrated && isAuthenticated && buyerGroupId) {
        const initializeBuyerProperties = () => {
          if (rawBuyerUnits?.length > 0) {
            setBuyerUnits(rawBuyerUnits as BuyerUnit[], buyerGroupId);
          }
          if (rawBuyerProjects?.length > 0) {
            setBuyerProjects(rawBuyerProjects as BuyerProject[], buyerGroupId);
          }
        };
        if (window.requestIdleCallback) {
          window.requestIdleCallback(initializeBuyerProperties, {
            timeout: 1000
          });
        } else {
          setTimeout(initializeBuyerProperties, 0);
        }
      }
    };
    t3 = [isHydrated, isAuthenticated, buyerGroupId, rawBuyerUnits, rawBuyerProjects, setBuyerUnits, setBuyerProjects];
    $[2] = buyerGroupId;
    $[3] = isAuthenticated;
    $[4] = isHydrated;
    $[5] = rawBuyerProjects;
    $[6] = rawBuyerUnits;
    $[7] = setBuyerProjects;
    $[8] = setBuyerUnits;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  const t4 = isHydrated && isAuthenticated && buyerGroupId && (rawBuyerUnits?.length > 0 || rawBuyerProjects?.length > 0);
  let t5;
  if ($[11] !== t4) {
    t5 = {
      isInitialized: t4
    };
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}